import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type BlockedState = 'banned' | 'unavailable' | 'pending' | 'rejected' | 'applicationRequired' | null;

// gloss
type BannedUserContextValue = {
  isBanned: boolean;
  blockedState: BlockedState;
  message: string | null;
  supportEmail: string | null;
  setBlocked: (state: BlockedState, message?: string, supportEmail?: string) => void;
};

const BannedUserContext = createContext<BannedUserContextValue | null>(null);

// gloss
export const BannedUserProvider = ({ children }: { children: ReactNode }) => {
  const [blockedState, setBlockedState] = useState<BlockedState>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [supportEmail, setSupportEmail] = useState<string | null>(null);

  const setBlocked = useCallback((state: BlockedState, msg?: string, appealEmail?: string) => {
    if (state === 'banned' && typeof window !== 'undefined') {
      window.localStorage.clear();
      window.sessionStorage.clear();
    }
    setBlockedState(state);
    setMessage(msg ?? null);
    setSupportEmail(appealEmail ?? null);
  }, []);

  const value = useMemo(
    () => ({
      isBanned: blockedState === 'banned',
      blockedState,
      message,
      supportEmail,
      setBlocked,
    }),
    [blockedState, message, supportEmail, setBlocked],
  );

  return <BannedUserContext.Provider value={value}>{children}</BannedUserContext.Provider>;
};

export const useBannedUser = (): BannedUserContextValue => {
  const ctx = useContext(BannedUserContext);
  if (!ctx) {
    throw new Error('useBannedUser must be used within BannedUserProvider');
  }
  return ctx;
};
